import React, { useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../config/utilities/useAuth";
import LoadingSpinner from "../../component/loading/LoadingSpinner";
import Title from "../../component/title/Title";
import HeaderImage from "../../image/header.png";
import SidebarAccount from "../sidebar/SidebarAccount";
import UsersIcon from "../sidebar/img/UsersIcon.svg";
import { BackButton } from "../../component/buttonComponent/ButtonComponent";
import { MasterAdminProvider, useMasterAdmin } from "./MasterAdminContext";
import InstitutionScopeBar from "./components/InstitutionScopeBar";
import "./MasterAdminLayout.css";

const NAV_ITEMS = [
  {
    label: "Institutions",
    path: "/master-admin/institutions",
    description: "Workspaces & configuration",
    icon: "institution",
  },
  {
    label: "Administrators",
    path: "/master-admin/administrators",
    description: "Platform admin accounts",
    icon: "admin",
  },
  {
    label: "Family Management",
    path: "/master-admin/users",
    description: "Manage families, parents & children",
    icon: "users",
  },
  {
    label: "Deeplinks",
    path: "/master-admin/deeplinks",
    description: "Custom sidebar links per institution",
    icon: "deeplink",
  },
];

const NavIcon = ({ type }) => {
  if (type === "users") {
    return <img src={UsersIcon} alt="" className="ma-nav-icon-img" />;
  }

  if (type === "admin") {
    return (
      <svg className="ma-nav-icon-svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M10 10.833a3.333 3.333 0 1 0 0-6.666 3.333 3.333 0 0 0 0 6.666ZM3.333 16.667c0-2.761 3.582-5 6.667-5s6.667 2.239 6.667 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "deeplink") {
    return (
      <svg className="ma-nav-icon-svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M8.333 11.667 11.667 8.333M7.5 13.75 6.25 15a2.083 2.083 0 1 1-2.946-2.946l1.25-1.25M12.5 6.25l1.25-1.25A2.083 2.083 0 1 1 16.696 7.946l-1.25 1.25M7.083 12.917 12.917 7.083"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg className="ma-nav-icon-svg" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M3.333 7.5 10 3.333 16.667 7.5V15.833a1.667 1.667 0 0 1-1.667 1.667H5a1.667 1.667 0 0 1-1.667-1.667V7.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 17.5v-5h5v5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const MasterAdminLayoutShell = () => {
  const location = useLocation();
  const { userId: user, loading, privilege, privilegeSelection } = useAuth();
  const { formView, selectedInstitution } = useMasterAdmin();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isSuperAdmin = useMemo(
    () =>
      privilege?.role === "SUPERADMIN" ||
      privilegeSelection?.some((p) => p.role === "SUPERADMIN"),
    [privilege?.role, privilegeSelection]
  );

  const needsInstitutionScope =
    location.pathname.startsWith("/master-admin/administrators") ||
    location.pathname.startsWith("/master-admin/users");

  const activeNav = NAV_ITEMS.find((item) => location.pathname.startsWith(item.path));

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="ma-loading">
        <LoadingSpinner size="large" color="#ffb502" />
      </div>
    );
  }

  if (!user || !isSuperAdmin) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="ma-layout">
      {sidebarOpen && (
        <button
          type="button"
          className="ma-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close navigation"
        />
      )}

      <aside className={`ma-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="ma-sidebar-brand">
          <img src={HeaderImage} alt="Bus Buddy" className="ma-sidebar-logo" />
        </div>

        <nav className="ma-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `ma-nav-link ${isActive ? "active" : ""}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="ma-nav-icon">
                <NavIcon type={item.icon} />
              </span>
              <span className="ma-nav-copy">
                <span className="ma-nav-label">{item.label}</span>
                <span className="ma-nav-description">{item.description}</span>
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-account-section">
          <SidebarAccount />
        </div>
      </aside>

      <div className={`ma-main ${formView ? "ma-main--form" : ""}`}>
        <header className={`ma-topbar ${formView ? "ma-topbar--form" : ""}`}>
          <button
            type="button"
            className="ma-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            ☰
          </button>
          {formView ? (
            <div className="ma-topbar-form">
              <BackButton
                label={formView.backLabel}
                btnClickBack={formView.onBack}
                fontSize="0.875rem"
                color="#101828"
                backgroundColor="#FFFFFF"
              />
              <div className="ma-topbar-form-copy">
                <Title
                  title={formView.title}
                  subtitle={formView.subtitle}
                  titleSize="1.5rem"
                  subtitleSize="1rem"
                  gap="0.5rem"
                  lineHeight="2rem"
                />
                {selectedInstitution && (
                  <div className="ma-topbar-institution">
                    <span className="ma-topbar-institution-label">Workspace</span>
                    <span className="ma-topbar-institution-name">
                      {selectedInstitution.institutionName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="ma-topbar-copy">
              <Title
                title={activeNav?.label ?? "Master Admin Console"}
                subtitle={activeNav?.description ?? "Platform-wide management"}
                titleSize="1.5rem"
                subtitleSize="1rem"
                gap="0.5rem"
                lineHeight="2rem"
              />
            </div>
          )}
        </header>

        {needsInstitutionScope && (
          <div
            className={`ma-scope-bar-slot ${
              formView ? "ma-scope-bar-slot--hidden" : ""
            }`}
          >
            <InstitutionScopeBar />
          </div>
        )}

        <div className={`ma-content ${formView ? "ma-content--form" : ""}`}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

const MasterAdminLayout = () => (
  <MasterAdminProvider>
    <MasterAdminLayoutShell />
  </MasterAdminProvider>
);

export default MasterAdminLayout;
